import React, { useState, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import { whatsNewApi } from "../services/api";

/**
 * What's New bell + auto-popup panel.
 *
 * Behaviour:
 *   - On mount, fetches announcements. If any are unread, the panel auto-opens
 *     so the user sees them without having to find the bell.
 *   - Each unread announcement has its own "Got it" button. Clicking it
 *     persists the read receipt on the server and removes that card from view.
 *   - The panel stays open while any unread items remain. Once the user has
 *     dismissed everything, the panel closes itself.
 *   - The bell stays in the header. Clicking it later re-opens the panel and
 *     shows ALL announcements (read + unread) for browsing history.
 */
const WhatsNewBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);            // full list (read + unread)
  const [loading, setLoading] = useState(false);
  const [autoShown, setAutoShown] = useState(false); // only auto-open once per page load

  const unreadItems = items.filter((a) => !a.is_read);
  const unreadCount = unreadItems.length;

  const fetchList = useCallback(async () => {
    try {
      const list = await whatsNewApi.list();
      setItems(Array.isArray(list) ? list : []);
      return list;
    } catch (err) {
      console.error("Failed to load announcements:", err);
      return [];
    }
  }, []);

  // Initial load — auto-open if there's anything unread
  useEffect(() => {
    (async () => {
      const list = await fetchList();
      if (!autoShown && Array.isArray(list) && list.some((a) => !a.is_read)) {
        setOpen(true);
        setAutoShown(true);
      }
    })();
    // Background refresh every 5 minutes so newly-published items appear
    const t = setInterval(fetchList, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchList, autoShown]);

  const openPanel = async () => {
    setOpen(true);
    setLoading(true);
    await fetchList();
    setLoading(false);
  };

  const closePanel = () => setOpen(false);

  const handleGotIt = async (ann) => {
    // Optimistic: hide the card immediately
    setItems((prev) => prev.map((a) => (a.id === ann.id ? { ...a, is_read: true } : a)));
    try {
      await whatsNewApi.markRead(ann.id);
    } catch (err) {
      // Revert on failure
      setItems((prev) => prev.map((a) => (a.id === ann.id ? { ...a, is_read: false } : a)));
      console.error("Mark-as-read failed:", err);
    }
  };

  // Auto-close once everything is dismissed during this session
  useEffect(() => {
    if (open && autoShown && unreadCount === 0) {
      // Tiny delay so the last card animates out cleanly
      const t = setTimeout(() => setOpen(false), 250);
      return () => clearTimeout(t);
    }
  }, [open, autoShown, unreadCount]);

  // When opened via bell click (not auto), show ALL items.
  // When opened automatically, show only unread.
  const displayItems = autoShown && unreadCount > 0 ? unreadItems : items;

  return (
    <>
      <button
        type="button"
        title="What's New"
        data-testid="whats-new-bell"
        onClick={open ? closePanel : openPanel}
        style={{
          position: "relative",
          width: "36px", height: "36px", borderRadius: "50%",
          border: "2px solid var(--primary-200, #bfdbfe)", background: "var(--primary-50, #eff6ff)",
          color: "var(--primary-600, #2563eb)", cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center",
          marginRight: "0.25rem", transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--primary-100, #dbeafe)";
          e.currentTarget.style.borderColor = "var(--primary-400, #60a5fa)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "var(--primary-50, #eff6ff)";
          e.currentTarget.style.borderColor = "var(--primary-200, #bfdbfe)";
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 003.4 0" />
        </svg>
        {unreadCount > 0 && (
          <span
            data-testid="whats-new-unread-badge"
            style={{
              position: "absolute", top: "-4px", right: "-4px",
              background: "var(--red-500, #ef4444)", color: "white",
              fontSize: "0.7rem", fontWeight: 700,
              minWidth: "18px", height: "18px", padding: "0 5px",
              borderRadius: "9999px",
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "2px solid white",
              lineHeight: 1,
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Click-outside backdrop — only when manually opened, never when auto-shown */}
          {!autoShown || unreadCount === 0 ? (
            <div
              onClick={closePanel}
              style={{ position: "fixed", inset: 0, background: "transparent", zIndex: 998 }}
            />
          ) : null}

          <div
            data-testid="whats-new-panel"
            style={{
              position: "fixed",
              top: "82px",
              right: "1.25rem",
              width: "min(440px, calc(100vw - 2rem))",
              maxHeight: "calc(100vh - 110px)",
              background: "white",
              border: "1px solid var(--slate-200, #e2e8f0)",
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.22)",
              borderRadius: "12px",
              overflow: "hidden",
              zIndex: 999,
              display: "flex",
              flexDirection: "column",
              animation: "wn-slide-in 0.22s ease-out",
            }}
          >
            {/* Inline keyframes — avoids touching the global stylesheet */}
            <style>{`
              @keyframes wn-slide-in {
                from { opacity: 0; transform: translateY(-12px); }
                to   { opacity: 1; transform: translateY(0); }
              }
            `}</style>

            {/* Green header */}
            <div
              style={{
                padding: "0.85rem 1rem",
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                color: "white",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 2l2.39 5.06L20 8l-4 4.4L17.21 19 12 16.27 6.79 19 8 12.4 4 8l5.61-.94L12 2z" />
                </svg>
                <strong style={{ fontSize: "1rem", letterSpacing: "0.01em" }}>What&apos;s New</strong>
              </div>
              <button
                onClick={closePanel}
                aria-label="Close"
                style={{
                  background: "none", border: "none", color: "white",
                  cursor: "pointer", fontSize: "1.25rem", lineHeight: 1,
                  padding: "0 0.2rem", opacity: 0.9,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.9)}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ overflowY: "auto", flex: 1, padding: "0.6rem 0.4rem" }}>
              {loading ? (
                <p style={{ padding: "1.25rem", textAlign: "center", color: "var(--slate-500)" }}>Loading…</p>
              ) : displayItems.length === 0 ? (
                <p style={{ padding: "1.5rem 1rem", textAlign: "center", color: "var(--slate-500)" }}>
                  No announcements right now.
                </p>
              ) : (
                displayItems.map((a) => (
                  <article
                    key={a.id}
                    data-testid={`whats-new-item-${a.id}`}
                    style={{
                      padding: "1rem 1.1rem",
                      borderBottom: "1px solid var(--slate-100)",
                    }}
                  >
                    <header style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "0.5rem", marginBottom: "0.4rem" }}>
                      <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "var(--slate-900)", lineHeight: 1.3 }}>
                        {a.title}
                      </h4>
                      <time style={{ fontSize: "0.72rem", color: "var(--slate-400)", whiteSpace: "nowrap" }}>
                        {a.published_at ? new Date(a.published_at).toLocaleDateString() : ""}
                      </time>
                    </header>
                    <div
                      className="whats-new-body"
                      style={{
                        fontSize: "0.9rem",
                        color: "var(--slate-700)",
                        lineHeight: 1.5,
                      }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.body_html || "") }}
                    />
                    {a.published_by_name && a.published_by_name !== "System" && (
                      <p style={{ fontSize: "0.72rem", color: "var(--slate-400)", margin: "0.4rem 0 0" }}>
                        — {a.published_by_name}
                      </p>
                    )}

                    {!a.is_read && (
                      <div style={{ marginTop: "0.85rem", display: "flex", justifyContent: "flex-end" }}>
                        <button
                          type="button"
                          onClick={() => handleGotIt(a)}
                          data-testid={`whats-new-got-it-${a.id}`}
                          style={{
                            background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            padding: "0.45rem 1.1rem",
                            fontSize: "0.88rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            boxShadow: "0 1px 3px rgba(5, 150, 105, 0.3)",
                            transition: "transform 0.1s, box-shadow 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = "0 4px 10px rgba(5, 150, 105, 0.35)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(5, 150, 105, 0.3)";
                          }}
                          onMouseDown={(e) => (e.currentTarget.style.transform = "translateY(1px)")}
                          onMouseUp={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                        >
                          Got it
                        </button>
                      </div>
                    )}
                  </article>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default WhatsNewBell;
